import { useState } from 'react';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Link2, Info, School } from 'lucide-react';
import { useIsInternalAdmin } from '@/hooks/useIsInternalAdmin';
import { DiscoverabilityControls } from '@/components/connect/DiscoverabilityControls';
import { ShopDirectory } from '@/components/connect/ShopDirectory';
import { ShopHiringControls } from '@/components/connect/ShopHiringControls';
import { TalentDirectory } from '@/components/connect/TalentDirectory';
import { ConnectionInbox } from '@/components/connect/ConnectionInbox';
import { OffersReceived } from '@/components/connect/OffersReceived';
import { OfferBuilder } from '@/components/connect/OfferBuilder';
import { OffersSent } from '@/components/connect/OffersSent';
import { WorkforceOpportunities } from '@/components/connect/WorkforceOpportunities';
import { WorkforceListingsManager } from '@/components/connect/WorkforceListingsManager';
import { useVisibilitySettings } from '@/hooks/useConnectApi';
import { useAuth } from '@/contexts/AuthContext';

const ConnectPage = () => {
  const navigate = useNavigate();
  const { isPassportMode } = useAuth();
  const connectEnabled = useFeatureFlag('optibayConnect');
  const { data: visibilitySettings } = useVisibilitySettings();

  // Passport mode: allow access if user has personally opted in OR feature enabled
  // Shop mode: require feature flag
  if (isPassportMode) {
    const hasAccess = connectEnabled || (visibilitySettings?.visible_to_shops === true);
    if (!hasAccess) {
      return (
        <div className="container mx-auto p-4 md:p-6 max-w-4xl">
          <Card>
            <CardHeader>
              <CardTitle>OptiBay Connect</CardTitle>
              <CardDescription>
                This feature is not yet enabled for your account. Contact support if you'd like early access.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" onClick={() => navigate('/passport')}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Back to Passport
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
  } else {
    if (!connectEnabled) {
      return (
        <div className="container mx-auto p-4 md:p-6 max-w-4xl">
          <Card>
            <CardHeader>
              <CardTitle>OptiBay Connect</CardTitle>
              <CardDescription>
                Connect is not yet enabled for your shop. Contact support if you'd like early access.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" onClick={() => navigate('/')}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
  }

  const isVisible = visibilitySettings?.visible_to_shops === true;

  return (
    <div className="container mx-auto p-4 md:p-6 space-y-6 max-w-4xl pb-20">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link2 className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-xl font-semibold">OptiBay Connect</h1>
            <p className="text-sm text-muted-foreground">
              {isPassportMode
                ? 'Manage your visibility and find recruiting shops.'
                : 'Manage hiring and find talent.'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <AdminSchoolsButton navigate={navigate} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(isPassportMode ? '/passport' : '/')}
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            {isPassportMode ? 'Passport' : 'Dashboard'}
          </Button>
        </div>
      </div>

      {isPassportMode ? (
        <>
          {/* Passport Mode: Discoverability + Recruiting Shops + Workforce + Inbox */}
          <DiscoverabilityControls />

          {!isVisible && (
            <Card>
              <CardContent className="flex items-center gap-3 py-4">
                <Info className="h-5 w-5 text-muted-foreground shrink-0" />
                <p className="text-sm text-muted-foreground">
                  Turn on visibility above to appear to shops and browse recruiting shops.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Workforce Opportunities — always visible for browsing, apply gated by visibility */}
          <WorkforceOpportunities />

          {isVisible && (
            <>
              <ShopDirectory />
              <ConnectionInbox />
              <OffersReceived />
            </>
          )}
        </>
      ) : (
        <ShopConnectView />
      )}
    </div>
  );
};

/** Shop-mode view extracted so we can hold counter-offer state */
function ShopConnectView() {
  const [counterPrefill, setCounterPrefill] = useState<import('@/components/connect/OfferBuilder').OfferPrefill | null>(null);
  return (
    <>
      <ShopHiringControls />
      <TalentDirectory />
      <WorkforceListingsManager />
      <ConnectionInbox />
      <OfferBuilder prefill={counterPrefill} onPrefillConsumed={() => setCounterPrefill(null)} />
      <OffersSent onCounterOffer={(p) => setCounterPrefill(p)} />
    </>
  );
}

function AdminSchoolsButton({ navigate }: { navigate: (path: string) => void }) {
  const { data: isAdmin } = useIsInternalAdmin();
  if (!isAdmin) return null;
  return (
    <Button variant="outline" size="sm" onClick={() => navigate('/admin/schools')} className="gap-1.5">
      <School className="h-4 w-4" />
      Admin: Schools
    </Button>
  );
}

export default ConnectPage;

